/* Course legs — geodesic geometry plus a feasibility estimate.
 *
 * UI-UX-DESIGN.md §5.2: course plotting is the most frequent single action in the
 * product, so it gets direct manipulation on the map with a live readout, not a dialog.
 *
 * §8 adds the part Command PE only offers inside its Operation Planner: a **live
 * feasibility estimate on an ordinary course**. Telling an operator "this leg puts you
 * 40 nm past bingo" while they are drawing it prevents the most common planning error
 * in the product.
 *
 * ── v0.9 HONESTY NOTE ────────────────────────────────────────────────────────
 * The endurance model here is a single figure per platform at current speed. Real fuel
 * burn varies with throttle, altitude, configuration and stores — which is why
 * `SPEC-01` §5 puts it in the Motion phase of the kernel. This is a planning estimate,
 * and the UI labels it as one.
 */
import { inverse, metresToNm } from './geodesy'
import type { OrbisEntity } from '../data/types'

export interface Waypoint {
  lon: number
  lat: number
}

export interface Leg {
  index: number
  from: Waypoint
  to: Waypoint
  distanceNm: number
  bearingDeg: number
  /** Hours to fly/steam this leg at the platform's current speed. */
  hours: number
  /** Cumulative hours from the platform's current position. */
  cumHours: number
  cumDistanceNm: number
  /** Fuel fraction remaining on arrival, 0-1. Negative means it does not get there. */
  fuelRemaining: number
  reachable: boolean
}

export interface CoursePlan {
  legs: Leg[]
  totalNm: number
  totalHours: number
  /** Fuel fraction at the final waypoint. Negative means the course is not flyable. */
  fuelAtEnd: number
  feasible: boolean
  /** Index of the first leg that cannot be completed, or null. */
  bingoAtLeg: number | null
  /** Distance the platform can still cover on remaining fuel, from its current position. */
  rangeRemainingNm: number
}

/** Zero speed means no course can be computed — a facility cannot be given one. */
export function canPlotCourse(e: OrbisEntity): boolean {
  return e.speedKt > 0 && e.enduranceHrs !== undefined
}

export function planCourse(entity: OrbisEntity, waypoints: Waypoint[]): CoursePlan {
  const speed = Math.max(entity.speedKt, 1)
  const endurance = entity.enduranceHrs ?? 0
  const hoursRemaining = endurance * entity.fuelFraction
  const rangeRemainingNm = hoursRemaining * speed

  const legs: Leg[] = []
  let cursor: Waypoint = { lon: entity.lon, lat: entity.lat }
  let cumHours = 0
  let cumDistanceNm = 0
  let bingoAtLeg: number | null = null

  waypoints.forEach((wp, i) => {
    const { distanceM, bearingDeg } = inverse(cursor, wp)
    const distanceNm = metresToNm(distanceM)
    const hours = distanceNm / speed
    cumHours += hours
    cumDistanceNm += distanceNm

    // Fraction of the platform's TOTAL endurance consumed, so the number lines up
    // with the fuel gauge the operator is already reading.
    const fuelRemaining =
      endurance > 0 ? entity.fuelFraction - cumHours / endurance : entity.fuelFraction
    const reachable = fuelRemaining >= 0
    if (!reachable && bingoAtLeg === null) bingoAtLeg = i

    legs.push({
      index: i,
      from: cursor,
      to: wp,
      distanceNm,
      bearingDeg,
      hours,
      cumHours,
      cumDistanceNm,
      fuelRemaining,
      reachable,
    })
    cursor = wp
  })

  const fuelAtEnd = legs.length ? legs[legs.length - 1].fuelRemaining : entity.fuelFraction

  return {
    legs,
    totalNm: cumDistanceNm,
    totalHours: cumHours,
    fuelAtEnd,
    feasible: bingoAtLeg === null,
    bingoAtLeg,
    rangeRemainingNm,
  }
}

/** Hours as `4h 20m` — operators read endurance that way, not as a decimal. */
export function hoursToHm(h: number): string {
  if (!isFinite(h) || h < 0) return '—'
  const total = Math.round(h * 60)
  const hh = Math.floor(total / 60)
  const mm = total % 60
  return hh > 0 ? `${hh}h ${String(mm).padStart(2, '0')}m` : `${mm}m`
}


// ── Which unit is a course being plotted for? ────────────────────────────────

export interface CourseSelectionLike {
  kind: 'entity' | 'contact'
  id: string
}

export type CoursesLike = Record<string, Waypoint[]>

/**
 * Resolve the unit a course belongs to, and the waypoints that belong to it.
 *
 * **The current selection wins over the plotting focus.** Selecting a different vehicle is
 * an explicit act by the operator, so it must take precedence — and because courses are now
 * stored per entity, switching units *reveals* that unit's existing plan instead of
 * discarding the previous one.
 *
 * Two bugs live in this function's history, both worth keeping in view:
 *
 * 1.  The precedence rule was originally inlined in both the map click handler and the
 *     course panel, and written backwards in both (`course.entityId ?? selection`), so once
 *     a course was started every later click extended the first unit's course regardless of
 *     selection.
 * 2.  Fixing that introduced a worse behaviour: the store held one course and cleared it on
 *     unit switch, so plotting for a second aircraft destroyed the first one's plan. A plan
 *     is a property of the unit, not of the tool.
 *
 * The `focus` argument is the unit whose course a click extends when nothing is selected —
 * without it, deselecting mid-plot would leave clicks with no target and draw nothing.
 */
export function resolveCourseTarget(
  selection: CourseSelectionLike | null,
  courses: CoursesLike,
  focus: string | null = null,
): { entityId: string | null; waypoints: Waypoint[] } {
  const selectedEntityId = selection?.kind === 'entity' ? selection.id : null
  const entityId = selectedEntityId ?? focus
  // Waypoints can only be read under the id they were stored under, so a unit can never be
  // shown another unit's legs under its own name.
  return { entityId, waypoints: entityId ? (courses[entityId] ?? []) : [] }
}
